<h2 class="px-3 text-center text-4xl tracking-wide md:text-left">
    Book your trip
</h2>
<p class="px-3 text-center text-lg text-deemphasized md:text-left">
    Don't see your date? Please
    <span
        class="cursor-pointer text-black underline"
        (click)="showProposeDateModal()"
        >propose a new departure</span
    >.
</p>

@if (
    tripForm &&
    (allDepartures | async).length > 0 &&
    !isDeparturesEmptyForSelectedItinerary
) {
    <form
        [formGroup]="tripForm"
        class="mb-4 grid w-full grid-cols-2 gap-4 px-3 md:grid-cols-10">
        <div
            #toggleButton
            class="relative my-2 w-full rounded-md border border-lightgrey bg-white md:col-span-3">
            <button
                (click)="toggleSelectMonthDropdown()"
                class="relative w-full cursor-pointer items-center justify-between px-3 py-2 md:px-4 md:py-3"
                aria-haspopup="listbox"
                aria-labelledby="dropdown-label">
                <p class="text-left text-sm font-semibold">When</p>
                <div class="flex items-center justify-between">
                    <span class="flex items-center gap-3 text-sm capitalize">
                        {{ tripForm.controls.selectedMonthYear.value.month }}
                        {{ tripForm.controls.selectedMonthYear.value.year }}
                    </span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none">
                        <path
                            d="M1 1L7 7L13 1"
                            stroke="#818181"
                            stroke-width="1.6"
                            stroke-linecap="round"
                            stroke-linejoin="round" />
                    </svg>
                </div>
            </button>
            @if (selectMonthDropdownOpen) {
                <div
                    #dropdownMenu
                    class="dropdown-list absolute top-full z-10 m-0 flex w-max flex-col gap-3 rounded bg-white p-4 shadow-md">
                    @for (
                        year of possibleYearsAndMonths | keyvalue;
                        track year
                    ) {
                        <div>
                            <p class="mb-2 ml-4 text-xl font-semibold">
                                {{ year.key }}
                            </p>
                            <div class="grid grid-cols-4 gap-1 md:grid-cols-6">
                                @for (month of year.value; track month) {
                                    <button
                                        class="m-0 w-full px-4 py-2 transition-none disabled:bg-lightergrey disabled:opacity-50"
                                        (click)="
                                            selectMonthAndYearFromDropdown(
                                                monthNames[month.index].value,
                                                +year.key
                                            )
                                        "
                                        [disabled]="month.count === 0"
                                        [ngClass]="{
                                            'bg-red text-white':
                                                monthNames[month.index]
                                                    .value ===
                                                    tripForm.controls
                                                        .selectedMonthYear.value
                                                        .month &&
                                                tripForm.controls
                                                    .selectedMonthYear.value
                                                    .year === +year.key,
                                        }">
                                        {{ monthNames[month.index].label }}
                                    </button>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
        <div
            #toggleItineraryDropdownButton
            class="relative my-2 w-full rounded-md border border-lightgrey bg-white md:col-span-3">
            <button
                (click)="toggleItineraryDropdown()"
                class="relative w-full cursor-pointer items-center justify-between px-3 py-2 md:px-4 md:py-3"
                aria-haspopup="listbox"
                aria-labelledby="dropdown-label">
                <p class="text-left text-sm font-semibold">Itinerary</p>
                <div class="flex items-center justify-between">
                    <span class="flex items-center gap-3 text-sm">
                        {{ getItinerariesSelected().length }} Selected
                    </span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none">
                        <path
                            d="M1 1L7 7L13 1"
                            stroke="#818181"
                            stroke-width="1.6"
                            stroke-linecap="round"
                            stroke-linejoin="round" />
                    </svg>
                </div>
            </button>
            @if (itineraryDropdownOpen) {
                <div
                    #itineraryDropdownMenu
                    class="dropdown-list absolute -left-1/3 top-full z-10 m-0 flex w-max flex-col gap-3 rounded bg-white p-4 shadow-md md:left-0">
                    <div class="flex flex-col gap-4">
                        @for (
                            control of tripForm.controls.itinerary.controls;
                            track control;
                            let i = $index
                        ) {
                            <div class="flex items-center gap-2">
                                <app-custom-checkbox [formControl]="control">
                                </app-custom-checkbox>
                                <p>{{ itinerariesForTrip[i].label }}</p>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    </form>
}

<div class="flex flex-col gap-2 px-3">
    @if (this.loadingFilteredDepartures) {
        <app-loader color="#ee5769"></app-loader>
    }

    @if (
        (allDepartures | async).length > 0 &&
        !isDeparturesEmptyForSelectedItinerary
    ) {
        <button
            (click)="showEarlierDates()"
            class="m-0 mb-2 w-full rounded-full border border-grey-150 px-2 py-2 text-sm disabled:bg-evenlightergrey disabled:opacity-50 md:w-1/3"
            [disabled]="(startIndex | async) === 0">
            Show earlier dates
            <svg
                class="ml-2 inline h-2 rotate-180 fill-current text-grey-800"
                viewBox="0 0 20 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M0.666666 2.33333C0.666056 2.02179 0.774557 1.71988 0.973331 1.48C1.08526 1.34498 1.22273 1.23338 1.37786 1.15158C1.53299 1.06978 1.70273 1.01938 1.87737 1.00328C2.052 0.987179 2.2281 1.00569 2.39557 1.05775C2.56304 1.10981 2.7186 1.19439 2.85333 1.30666L10 7.28L17.16 1.52C17.2964 1.40924 17.4533 1.32653 17.6218 1.27662C17.7902 1.22672 17.9669 1.21059 18.1416 1.22918C18.3163 1.24776 18.4856 1.30069 18.6398 1.38492C18.794 1.46915 18.93 1.58303 19.04 1.72C19.1614 1.85795 19.2529 2.01951 19.3089 2.19452C19.3649 2.36954 19.3841 2.55424 19.3653 2.73703C19.3466 2.91982 19.2902 3.09676 19.1998 3.25673C19.1094 3.41671 18.9869 3.55627 18.84 3.66666L10.84 10.1067C10.6014 10.3028 10.3022 10.41 9.99333 10.41C9.6845 10.41 9.38524 10.3028 9.14666 10.1067L1.14667 3.44C0.985309 3.30624 0.857757 3.13633 0.774359 2.94405C0.69096 2.75177 0.654064 2.54254 0.666666 2.33333Z" />
            </svg>
        </button>
    }
    @for (
        departure of filteredDepartures | async;
        track departure.id || departure
    ) {
        <div>
            @if (departure.type !== 'EMPTY_SECTION' && departure.sectionName) {
                <div>
                    <p class="my-2 ml-4 text-2xl font-semibold">
                        {{ departure.sectionName }}
                    </p>
                </div>
            }
            @if (!departure.sectionName) {
                <app-trips-trip-self-booking-departure-detail
                    [trip]="trip"
                    [departure]="departure"
                    [isItineraryPage]="!!itineraryPageId">
                </app-trips-trip-self-booking-departure-detail>
            }
            @if (departure.type === 'EMPTY_SECTION') {
                <div class="my-4">
                    <p class="my-2 ml-4 text-2xl font-semibold text-lightgrey">
                        {{ departure.sectionName }}
                    </p>
                    <p class="ml-4 text-lightgrey">No departures this month</p>
                </div>
            }
        </div>
    }

    @if (
        (allDepartures | async).length > 0 &&
        !isDeparturesEmptyForSelectedItinerary
    ) {
        <button
            (click)="showLaterDates()"
            class="m-0 w-full rounded-full border border-grey-150 px-2 py-2 text-sm disabled:to-grey-100 disabled:opacity-50 md:w-1/3"
            [disabled]="(endIndex | async) >= (allDepartures | async).length">
            Show later dates
            <svg
                class="ml-2 inline h-2 fill-current text-grey-800"
                viewBox="0 0 20 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M0.666666 2.33333C0.666056 2.02179 0.774557 1.71988 0.973331 1.48C1.08526 1.34498 1.22273 1.23338 1.37786 1.15158C1.53299 1.06978 1.70273 1.01938 1.87737 1.00328C2.052 0.987179 2.2281 1.00569 2.39557 1.05775C2.56304 1.10981 2.7186 1.19439 2.85333 1.30666L10 7.28L17.16 1.52C17.2964 1.40924 17.4533 1.32653 17.6218 1.27662C17.7902 1.22672 17.9669 1.21059 18.1416 1.22918C18.3163 1.24776 18.4856 1.30069 18.6398 1.38492C18.794 1.46915 18.93 1.58303 19.04 1.72C19.1614 1.85795 19.2529 2.01951 19.3089 2.19452C19.3649 2.36954 19.3841 2.55424 19.3653 2.73703C19.3466 2.91982 19.2902 3.09676 19.1998 3.25673C19.1094 3.41671 18.9869 3.55627 18.84 3.66666L10.84 10.1067C10.6014 10.3028 10.3022 10.41 9.99333 10.41C9.6845 10.41 9.38524 10.3028 9.14666 10.1067L1.14667 3.44C0.985309 3.30624 0.857757 3.13633 0.774359 2.94405C0.69096 2.75177 0.654064 2.54254 0.666666 2.33333Z" />
            </svg>
        </button>
    }
</div>

<div
    class="my-8 mt-12 flex flex-col items-center"
    [ngClass]="(allDepartures | async).length === 0 ? 'mt-8' : ''">
    <img src="/assets/images/trips/calendar-icon.svg" class="mb-4" />
    <h3 class="text-2xl font-medium">Don't see your dates?</h3>
    <p class="mb-4 text-grey-500">We can make it happen!</p>

    <app-cta-button
        type="button"
        [additionalClasses]="['mt-4']"
        (onClick)="showProposeDateModal()">
        Propose Date
    </app-cta-button>
</div>
